import type { VisitActivityDefinition } from "@/types/domain";

export interface FieldVisitLocalContext {
  visitId: string;
  planId?: string;
  targetId?: string;
  farmerId: string;
  farmerName: string;
  farmId: string;
  farmName: string;
  plotIds: string[];
  plotNames: string[];
  assignedStaffName?: string;
  reasonIds: string[];
  reasonNames: string[];
  activityCatalogue: VisitActivityDefinition[];
  startedAt: string;
  latitude: number;
  longitude: number;
  gpsAccuracyMetres: number;
  evidenceTypes: string[];
  activityCount: number;
  followUpCount: number;
  observations?: string;
}

const keyFor = (visitId: string) => `aczon_field_visit_context_${visitId}`;

export const saveFieldVisitContext = (context: FieldVisitLocalContext) => {
  localStorage.setItem(keyFor(context.visitId), JSON.stringify(context));
};

export const loadFieldVisitContext = (visitId: string): FieldVisitLocalContext | undefined => {
  const raw = localStorage.getItem(keyFor(visitId));
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as FieldVisitLocalContext;
  } catch {
    return undefined;
  }
};

export const patchFieldVisitContext = (
  visitId: string,
  changes: Partial<FieldVisitLocalContext>,
): FieldVisitLocalContext | undefined => {
  const current = loadFieldVisitContext(visitId);
  if (!current) return undefined;
  const updated = { ...current, ...changes };
  saveFieldVisitContext(updated);
  return updated;
};

export const clearFieldVisitContext = (visitId: string) => {
  localStorage.removeItem(keyFor(visitId));
};

export interface CapturedPosition {
  latitude: number;
  longitude: number;
  accuracyMetres: number;
}

export const captureCurrentPosition = (): Promise<CapturedPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("GPS is not available on this device."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracyMetres: position.coords.accuracy,
      }),
      (reason) => reject(new Error(reason.message || "GPS position could not be captured.")),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });
